import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { FileText, Wand2 } from "lucide-react";

function renderMd(text) {
  // very small markdown renderer: paragraphs, **bold**, bullets, headings
  const lines = text.split("\n");
  const out = [];
  let listBuffer = [];
  const flushList = (key) => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-3 text-[#2D2926]">
        {listBuffer.map((l, i) => (
          <li
            key={i}
            dangerouslySetInnerHTML={{
              __html: inline(l.replace(/^[-*]\s+/, "")),
            }}
          />
        ))}
      </ul>
    );
    listBuffer = [];
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^#{1,6}\s+/.test(line)) {
      flushList(idx);
      const level = line.match(/^#+/)[0].length;
      const txt = line.replace(/^#+\s+/, "");
      const cls =
        level <= 2
          ? "font-serif text-2xl text-[#2D2926] mt-4 mb-2"
          : "font-serif text-xl text-[#2D2926] mt-3 mb-1";
      out.push(<div key={`h-${idx}`} className={cls} dangerouslySetInnerHTML={{ __html: inline(txt) }} />);
    } else if (/^[-*]\s+/.test(line)) {
      listBuffer.push(line);
    } else if (line.trim() === "") {
      flushList(idx);
    } else {
      flushList(idx);
      out.push(
        <p key={`p-${idx}`} className="leading-relaxed text-[#2D2926] my-2" dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  });
  flushList("end");
  return out;
}

function inline(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class=\"bg-[#F2EFE9] px-1 rounded\">$1</code>");
}

export default function Feedback() {
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState("");
  const [artifact, setArtifact] = useState("");
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/personas").then((r) => {
      setPersonas(r.data);
      if (r.data.length > 0) setPersonaId(r.data[0].persona_id);
    });
    api.get("/feedback").then((r) => setHistory(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!personaId || artifact.trim().length < 10) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/feedback", {
        persona_id: personaId,
        artifact,
        focus: focus || null,
      });
      setResult(data);
      toast.success("Feedback ready");
      const h = await api.get("/feedback");
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="feedback-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 anim-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-2">
            Work artifact feedback
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
            Paste it. Get honest, structured feedback.
          </h1>
          <p className="mt-3 text-[#6B655D] max-w-2xl">
            Drop a doc, an email draft, a deck blurb, or a Slack post. Pick which Colleague
            should review it. You'll get strengths, concerns, and specific suggestions.
          </p>
        </header>

        <form
          onSubmit={submit}
          data-testid="feedback-form"
          className="surface-card p-6 sm:p-8 mb-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="md:col-span-1 space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                Reviewer
              </label>
              <select
                value={personaId}
                onChange={(e) => setPersonaId(e.target.value)}
                data-testid="feedback-persona"
                className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
              >
                {personas.length === 0 ? (
                  <option value="">Create a Colleague first</option>
                ) : (
                  personas.map((p) => (
                    <option key={p.persona_id} value={p.persona_id}>
                      {p.name} ({p.role || "Colleague"})
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                Focus area (optional)
              </label>
              <input
                data-testid="feedback-focus"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder="e.g. Is the ask clear?"
                className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
              />
            </div>
            <button
              type="submit"
              data-testid="feedback-submit"
              disabled={busy || !personaId || artifact.trim().length < 10}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-3 text-sm transition-colors disabled:opacity-60"
            >
              <Wand2 size={16} strokeWidth={1.5} />
              {busy ? "Reading it carefully…" : "Get feedback"}
            </button>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
              Your artifact
            </label>
            <textarea
              value={artifact}
              onChange={(e) => setArtifact(e.target.value)}
              data-testid="feedback-artifact"
              rows={12}
              minLength={10}
              maxLength={20000}
              placeholder="Paste your draft here…"
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] leading-relaxed text-sm"
            />
          </div>
        </form>

        {result && (
          <section
            data-testid="feedback-result"
            className="surface-card p-6 sm:p-8 mb-12 anim-fade-up"
          >
            <p className="text-xs uppercase tracking-wider text-[#9A938A] mb-3">
              Feedback from {result.persona_name}
            </p>
            <div className="font-body">{renderMd(result.feedback)}</div>
          </section>
        )}

        {history.length > 0 && (
          <section>
            <h2 className="font-serif text-3xl text-[#2D2926] mb-4">Past reviews</h2>
            <ul className="surface-card divide-y divide-[#E6E2D8] overflow-hidden">
              {history.slice(0, 10).map((h) => (
                <li
                  key={h.feedback_id}
                  data-testid={`history-${h.feedback_id}`}
                  className="px-6 py-4 hover:bg-[#F2EFE9] transition-colors cursor-pointer"
                  onClick={() => setResult(h)}
                >
                  <div className="flex items-start gap-3">
                    <FileText size={16} strokeWidth={1.5} className="text-[#9A938A] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#2D2926] truncate">
                        {h.artifact_excerpt}
                      </p>
                      <p className="text-xs text-[#9A938A] uppercase tracking-wider mt-1">
                        {h.persona_name} ·{" "}
                        {new Date(h.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
